(function () {
  "use strict";

  var DEFAULTS = {
    rootSelector: "[data-approver-review-root]",
    reviewEndpoint: "/apps/b2b-submit-order/review-draft",
    loadingText: "Loading order review…",
  };

  function init() {
    var roots = document.querySelectorAll(DEFAULTS.rootSelector);
    if (!roots.length) return;

    Array.prototype.forEach.call(roots, function (root) {
      initRoot(root);
    });
  }

  function initRoot(root) {
    var config = getConfig(root);
    var params = getRequestParams();

    if (!params.shop || !params.draftOrderId || !params.token) {
      renderError(
        root,
        "This review link is incomplete. Please use the full link from the approval email."
      );
      return;
    }

    var state = {
      root: root,
      config: config,
      params: params,
      data: null,
      deleted: false,
      deletedInfo: null,
      flashMessage: "",
      flashType: "success",
      busyAction: "",
      editableLines: [],
      pendingPurchaseOrderNumber: "",
      pendingRejectionReason: "",
    };

    root.__approverReviewState = state;
    loadReview(state);
  }

  function getConfig(root) {
    var reviewEndpoint =
      root.getAttribute("data-review-endpoint") || DEFAULTS.reviewEndpoint;

    return {
      reviewEndpoint: reviewEndpoint,
      updateEndpoint: reviewEndpoint.replace(/\/review-draft$/, "/update-draft"),
      approveEndpoint: reviewEndpoint.replace(/\/review-draft$/, "/approve-draft"),
      rejectEndpoint: reviewEndpoint.replace(/\/review-draft$/, "/reject-draft"),
    };
  }

  function getRequestParams() {
    var search = new URLSearchParams(window.location.search);

    return {
      shop: trim(search.get("shop")),
      draftOrderId: trim(search.get("draftOrderId")),
      token: trim(search.get("token")),
    };
  }

  function buildUrl(endpoint, params) {
    var url = new URL(endpoint, window.location.origin);
    if (params.shop) url.searchParams.set("shop", params.shop);
    if (params.draftOrderId) url.searchParams.set("draftOrderId", params.draftOrderId);
    if (params.token) url.searchParams.set("token", params.token);
    return url.toString();
  }

  function trim(value) {
    return typeof value === "string" ? value.trim() : "";
  }

  function toSafeInteger(value, fallback) {
    var number = Number(value);
    if (!isFinite(number)) return fallback;
    return Math.round(number);
  }

  function escapeHtml(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function normalizeError(result, fallback) {
    if (!result) return fallback;

    if (Array.isArray(result.userErrors) && result.userErrors.length) {
      return result.userErrors
        .map(function (item) {
          return trim(item && item.message);
        })
        .filter(Boolean)
        .join(" | ");
    }

    if (trim(result.error)) return trim(result.error);
    if (trim(result.message)) return trim(result.message);

    return fallback;
  }

  function fetchJson(url, options) {
    var requestOptions = options || {};

    return fetch(url, requestOptions).then(function (response) {
      return response.text().then(function (text) {
        var parsed = null;

        try {
          parsed = text ? JSON.parse(text) : {};
        } catch (error) {
          parsed = null;
        }

        if (!parsed) {
          throw new Error(
            "The server returned an invalid response. Status " +
              response.status +
              "."
          );
        }

        if (!response.ok || parsed.ok === false) {
          throw new Error(
            normalizeError(
              parsed,
              "The request could not be completed. Status " + response.status + "."
            )
          );
        }

        return parsed;
      });
    });
  }

  function loadReview(state) {
    state.busyAction = "loading";
    renderLoading(state.root);

    fetchJson(buildUrl(state.config.reviewEndpoint, state.params), {
      method: "GET",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
      },
    })
      .then(function (result) {
        state.data = result;
        state.deleted = false;
        state.deletedInfo = null;
        state.flashMessage = "";
        state.busyAction = "";
        initializeEditableState(state);
        renderApp(state);
      })
      .catch(function (error) {
        state.busyAction = "";
        renderError(
          state.root,
          error && error.message
            ? error.message
            : "Unable to load this order review."
        );
      });
  }

  function initializeEditableState(state) {
    var data = state.data || {};
    var purchaseOrder = data.purchaseOrder || {};

    state.editableLines = normalizeEditableLines(data.lineItems || []);
    state.pendingPurchaseOrderNumber = trim(purchaseOrder.value || "");
    state.pendingRejectionReason = "";
  }

  function normalizeEditableLines(lineItems) {
    return (Array.isArray(lineItems) ? lineItems : []).map(function (item) {
      return {
        title: trim(item && item.title) || "Untitled item",
        sku: trim(item && item.sku) || "—",
        variantId: trim(item && item.variantId),
        quantity: Math.max(1, toSafeInteger(item && item.quantity, 1)),
        removed: false,
      };
    });
  }

  function postAction(state, endpoint, payload) {
    return fetchJson(buildUrl(endpoint, state.params), {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload || {}),
    });
  }

  function renderLoading(root) {
    root.innerHTML =
      '<div class="approver-review__state approver-review__state--loading">' +
      escapeHtml(DEFAULTS.loadingText) +
      "</div>";
  }

  function renderError(root, message) {
    root.innerHTML =
      '<div class="approver-review__state approver-review__state--error">' +
      '<p class="approver-review__message approver-review__message--error">' +
      escapeHtml(message || "Something went wrong.") +
      "</p>" +
      "</div>";
  }

  function renderApp(state) {
    if (state.deleted) {
      renderDeletedState(state);
      return;
    }

    var data = state.data || {};
    var draftOrder = data.draftOrder || {};
    var workflow = data.workflow || {};
    var company = data.company || {};
    var customMap = toAttributeMap(data.customAttributes || []);
    var invoiceUrl = trim(draftOrder.invoiceUrl);
    var isBusy = Boolean(state.busyAction);
    var workflowState = trim(workflow.state) || "submitted";
    var isFinalState =
      workflowState === "approved" || workflowState === "rejected";
    var completedOrder =
      data.order ||
      data.completedOrder ||
      (draftOrder && draftOrder.order) ||
      {};
    var activeLines = getActiveLines(state);
    var activePayloadLines = getActivePayloadLines(state);

    var flashHtml = state.flashMessage
      ? '<div class="approver-review__message approver-review__message--' +
        escapeHtml(state.flashType || "success") +
        '">' +
        escapeHtml(state.flashMessage) +
        "</div>"
      : "";

    var invoiceHtml = invoiceUrl
      ? '<a class="approver-review__link" href="' +
        escapeHtml(invoiceUrl) +
        '" target="_blank" rel="noopener noreferrer">Open draft invoice</a>'
      : '<span class="approver-review__muted">Invoice link not available yet</span>';

    var rowsHtml = activeLines.length
      ? activeLines
          .map(function (line) {
            var item = line.item;
            var title = trim(item.title) || "Untitled item";
            var sku = trim(item.sku) || "—";
            var variantId = trim(item.variantId);
            var qty = Math.max(1, toSafeInteger(item.quantity, 1));
            var quantityDisabledAttr =
              isFinalState || !variantId ? " disabled" : "";
            var removeDisabledAttr =
              isBusy || isFinalState || !variantId || activePayloadLines.length <= 1
                ? " disabled"
                : "";

            return (
              '<tr class="approver-review__row" data-line-row data-line-index="' +
              escapeHtml(String(line.index)) +
              '" data-variant-id="' +
              escapeHtml(variantId) +
              '">' +
              '<td class="approver-review__cell">' +
              escapeHtml(title) +
              "</td>" +
              '<td class="approver-review__cell">' +
              escapeHtml(sku) +
              "</td>" +
              '<td class="approver-review__cell approver-review__cell--qty">' +
              '<label class="visually-hidden" for="approver-review-qty-' +
              line.index +
              '">Quantity for ' +
              escapeHtml(title) +
              "</label>" +
              '<input id="approver-review-qty-' +
              line.index +
              '" class="approver-review__qty-input" type="number" min="1" step="1" value="' +
              escapeHtml(String(qty)) +
              '"' +
              quantityDisabledAttr +
              " />" +
              "</td>" +
              '<td class="approver-review__cell approver-review__cell--actions">' +
              '<button type="button" class="approver-review__button approver-review__button--secondary approver-review__button--row-action" data-action="remove-item" data-line-index="' +
              escapeHtml(String(line.index)) +
              '"' +
              removeDisabledAttr +
              ">" +
              "Remove" +
              "</button>" +
              "</td>" +
              "</tr>"
            );
          })
          .join("")
      : '<tr><td class="approver-review__cell" colspan="4">No line items found.</td></tr>';

    var editedAt = trim(customMap["Edited At"]);
    var approvedAt = trim(customMap["Approved At"]);
    var rejectedAt = trim(customMap["Rejected At"]);
    var completedOrderName =
      trim(completedOrder.name) || trim(completedOrder.orderNumber);

    state.root.innerHTML =
      flashHtml +
      '<div class="approver-review__header">' +
      '<div>' +
      '<h2 class="approver-review__title">Order review ' +
      escapeHtml(trim(draftOrder.name) || "") +
      "</h2>" +
      '<p class="approver-review__subtitle">Review, edit, approve, or reject this draft order.</p>' +
      "</div>" +
      '<div class="approver-review__badges">' +
      '<span class="approver-review__badge approver-review__badge--workflow">' +
      escapeHtml(formatStateLabel(workflowState)) +
      "</span>" +
      '<span class="approver-review__badge approver-review__badge--status">' +
      escapeHtml(trim(draftOrder.status) || "Unknown") +
      "</span>" +
      "</div>" +
      "</div>" +
      '<div class="approver-review__grid">' +
      '<section class="approver-review__card">' +
      '<h3 class="approver-review__card-title">Order</h3>' +
      buildMetaRow("Draft order", draftOrder.name || "—") +
      (completedOrderName
        ? buildMetaRow("Completed order", completedOrderName)
        : "") +
      buildMetaRow("Invoice", invoiceHtml, true) +
      buildMetaRow("Workflow state", formatStateLabel(workflowState)) +
      (editedAt ? buildMetaRow("Last edited", editedAt) : "") +
      (approvedAt ? buildMetaRow("Approved at", approvedAt) : "") +
      (rejectedAt ? buildMetaRow("Rejected at", rejectedAt) : "") +
      "</section>" +
      '<section class="approver-review__card">' +
      '<h3 class="approver-review__card-title">Company</h3>' +
      buildMetaRow("Company", company.name || "—") +
      buildMetaRow("Location", company.locationName || "—") +
      buildMetaRow(
        "Purchase order",
        state.pendingPurchaseOrderNumber || "Not provided"
      ) +
      "</section>" +
      '<section class="approver-review__card">' +
      '<h3 class="approver-review__card-title">Submitted by</h3>' +
      buildMetaRow("Customer", workflow.submittedByCustomerName || "—") +
      buildMetaRow("Email", workflow.submittedByCustomerEmail || "—") +
      buildMetaRow("Customer ID", workflow.submittedByCustomerId || "—") +
      buildMetaRow("Approver email", workflow.approverEmail || "—") +
      "</section>" +
      "</div>" +
      '<section class="approver-review__card approver-review__card--table">' +
      '<div class="approver-review__table-header">' +
      '<h3 class="approver-review__card-title">Line items</h3>' +
      '<p class="approver-review__muted">Adjust quantities or remove items before approval. At least one item must remain.</p>' +
      "</div>" +
      '<div class="approver-review__table-wrap">' +
      '<table class="approver-review__table">' +
      "<thead>" +
      "<tr>" +
      '<th class="approver-review__th">Item</th>' +
      '<th class="approver-review__th">SKU</th>' +
      '<th class="approver-review__th">Qty</th>' +
      '<th class="approver-review__th">Action</th>' +
      "</tr>" +
      "</thead>" +
      "<tbody>" +
      rowsHtml +
      "</tbody>" +
      "</table>" +
      "</div>" +
      "</section>" +
      '<section class="approver-review__card">' +
      '<h3 class="approver-review__card-title">Edit details</h3>' +
      '<div class="approver-review__field">' +
      '<label class="approver-review__label" for="approver-review-po-number">Purchase order number</label>' +
      '<input id="approver-review-po-number" class="approver-review__input" type="text" value="' +
      escapeHtml(state.pendingPurchaseOrderNumber || "") +
      '"' +
      (isFinalState ? " disabled" : "") +
      " />" +
      "</div>" +
      '<div class="approver-review__field">' +
      '<label class="approver-review__label" for="approver-review-rejection-reason">Rejection reason</label>' +
      '<textarea id="approver-review-rejection-reason" class="approver-review__textarea" rows="3"' +
      (isFinalState ? " disabled" : "") +
      ' placeholder="Optional reason shown in the rejection response.">' +
      escapeHtml(state.pendingRejectionReason || "") +
      "</textarea>" +
      "</div>" +
      '<div class="approver-review__actions">' +
      '<button type="button" class="approver-review__button approver-review__button--secondary" data-action="save"' +
      (isBusy || isFinalState ? " disabled" : "") +
      ">" +
      (state.busyAction === "save" ? "Saving…" : "Save changes") +
      "</button>" +
      '<button type="button" class="approver-review__button approver-review__button--primary" data-action="approve"' +
      (isBusy || isFinalState ? " disabled" : "") +
      ">" +
      (state.busyAction === "approve" ? "Approving…" : "Approve order") +
      "</button>" +
      '<button type="button" class="approver-review__button approver-review__button--danger" data-action="reject"' +
      (isBusy || isFinalState ? " disabled" : "") +
      ">" +
      (state.busyAction === "reject" ? "Rejecting…" : "Reject order") +
      "</button>" +
      "</div>" +
      (isFinalState
        ? '<p class="approver-review__muted">This draft order is already in a final workflow state.</p>'
        : "") +
      "</section>";

    attachEventHandlers(state);
  }

  function renderDeletedState(state) {
    var info = state.deletedInfo || {};
    var rejectedAt =
      info.workflow && trim(info.workflow.rejectedAt)
        ? trim(info.workflow.rejectedAt)
        : "";
    var rejectionReason =
      info.workflow && trim(info.workflow.rejectionReason)
        ? trim(info.workflow.rejectionReason)
        : "";

    state.root.innerHTML =
      '<div class="approver-review__state approver-review__state--success">' +
      '<h2 class="approver-review__title">Draft order rejected</h2>' +
      '<p class="approver-review__message approver-review__message--success">The draft order was deleted successfully.</p>' +
      (rejectionReason
        ? '<p class="approver-review__meta"><span class="approver-review__meta-label">Reason</span><span class="approver-review__meta-value">' +
          escapeHtml(rejectionReason) +
          "</span></p>"
        : "") +
      (rejectedAt
        ? '<p class="approver-review__meta"><span class="approver-review__meta-label">Rejected at</span><span class="approver-review__meta-value">' +
          escapeHtml(rejectedAt) +
          "</span></p>"
        : "") +
      "</div>";
  }

  function buildMetaRow(label, value, isHtmlValue) {
    return (
      '<div class="approver-review__meta">' +
      '<div class="approver-review__meta-label">' +
      escapeHtml(label) +
      "</div>" +
      '<div class="approver-review__meta-value">' +
      (isHtmlValue ? value : escapeHtml(value)) +
      "</div>" +
      "</div>"
    );
  }

  function formatStateLabel(value) {
    var text = trim(value || "");
    if (!text) return "Unknown";

    return text
      .split(/[_\s-]+/)
      .map(function (part) {
        return part ? part.charAt(0).toUpperCase() + part.slice(1) : "";
      })
      .join(" ");
  }

  function toAttributeMap(attributes) {
    return (attributes || []).reduce(function (acc, item) {
      var key = trim(item && item.key);
      if (!key) return acc;
      acc[key] = trim(item && item.value);
      return acc;
    }, {});
  }

  function attachEventHandlers(state) {
    var root = state.root;
    var saveButton = root.querySelector('[data-action="save"]');
    var approveButton = root.querySelector('[data-action="approve"]');
    var rejectButton = root.querySelector('[data-action="reject"]');
    var removeButtons = root.querySelectorAll('[data-action="remove-item"]');

    if (saveButton) {
      saveButton.addEventListener("click", function () {
        handleSave(state);
      });
    }

    if (approveButton) {
      approveButton.addEventListener("click", function () {
        handleApprove(state);
      });
    }

    if (rejectButton) {
      rejectButton.addEventListener("click", function () {
        handleReject(state);
      });
    }

    Array.prototype.forEach.call(removeButtons, function (button) {
      button.addEventListener("click", function () {
        var lineIndex = toSafeInteger(button.getAttribute("data-line-index"), -1);
        handleRemoveItem(state, lineIndex);
      });
    });
  }

  function readCurrentFormState(state) {
    var poInput = state.root.querySelector("#approver-review-po-number");
    var rejectionInput = state.root.querySelector("#approver-review-rejection-reason");
    var rows = state.root.querySelectorAll("[data-line-row]");

    state.pendingPurchaseOrderNumber = trim(poInput && poInput.value);
    state.pendingRejectionReason = trim(rejectionInput && rejectionInput.value);

    Array.prototype.forEach.call(rows, function (row) {
      var lineIndex = toSafeInteger(row.getAttribute("data-line-index"), -1);
      var input = row.querySelector("input[type='number']");
      var line = state.editableLines[lineIndex];

      if (!line || line.removed) return;

      line.quantity = Math.max(
        1,
        toSafeInteger(input && input.value, line.quantity || 1)
      );
    });
  }

  function getActiveLines(state) {
    return (state.editableLines || [])
      .map(function (item, index) {
        return { item: item, index: index };
      })
      .filter(function (entry) {
        return entry.item && !entry.item.removed;
      });
  }

  function getActivePayloadLines(state) {
    return getActiveLines(state)
      .map(function (entry) {
        return entry.item;
      })
      .filter(function (item) {
        return trim(item && item.variantId);
      });
  }

  function collectLines(state) {
    readCurrentFormState(state);

    return getActivePayloadLines(state).map(function (item) {
      return {
        variantId: trim(item.variantId),
        quantity: Math.max(1, toSafeInteger(item.quantity, 1)),
      };
    });
  }

  function getPurchaseOrderValue(state) {
    readCurrentFormState(state);
    return state.pendingPurchaseOrderNumber;
  }

  function getRejectionReason(state) {
    readCurrentFormState(state);
    return state.pendingRejectionReason;
  }

  function buildUpdatePayload(state) {
    return {
      draftOrderId: state.params.draftOrderId,
      token: state.params.token,
      purchaseOrderNumber: getPurchaseOrderValue(state),
      lines: collectLines(state),
    };
  }

  function setFlash(state, type, message) {
    state.flashType = type || "success";
    state.flashMessage = message || "";
  }

  function validateBeforeSubmit(state, payload) {
    if (!payload || !Array.isArray(payload.lines) || !payload.lines.length) {
      return "At least one item must remain on the draft order. If the order should not proceed, reject it instead.";
    }

    return "";
  }

  function handleRemoveItem(state, lineIndex) {
    if (state.busyAction) return;

    readCurrentFormState(state);

    var line = state.editableLines[lineIndex];
    if (!line || line.removed) return;

    if (!trim(line.variantId)) {
      setFlash(
        state,
        "error",
        "This item cannot be removed from the order in the current workflow."
      );
      renderApp(state);
      return;
    }

    if (getActivePayloadLines(state).length <= 1) {
      setFlash(
        state,
        "error",
        "At least one item must remain on the draft order. Reject the order instead if it should not proceed."
      );
      renderApp(state);
      return;
    }

    if (!window.confirm('Remove "' + (line.title || "this item") + '" from the order?')) {
      return;
    }

    line.removed = true;
    setFlash(
      state,
      "success",
      "Item removed from the order. Click Save changes to keep this update."
    );
    renderApp(state);
  }

  function handleSave(state) {
    if (state.busyAction) return;

    var payload = buildUpdatePayload(state);
    var validationMessage = validateBeforeSubmit(state, payload);

    if (validationMessage) {
      setFlash(state, "error", validationMessage);
      renderApp(state);
      return;
    }

    state.busyAction = "save";
    renderApp(state);

    postAction(state, state.config.updateEndpoint, payload)
      .then(function (result) {
        state.data = result;
        state.busyAction = "";
        initializeEditableState(state);
        setFlash(state, "success", "Order changes updated successfully.");
        renderApp(state);
      })
      .catch(function (error) {
        state.busyAction = "";
        setFlash(
          state,
          "error",
          error && error.message ? error.message : "Unable to save changes."
        );
        renderApp(state);
      });
  }

  function handleApprove(state) {
    if (state.busyAction) return;

    var payload = buildUpdatePayload(state);
    var validationMessage = validateBeforeSubmit(state, payload);

    if (validationMessage) {
      setFlash(state, "error", validationMessage);
      renderApp(state);
      return;
    }

    if (!window.confirm("Approve this order?")) {
      return;
    }

    state.busyAction = "approve";
    renderApp(state);

    postAction(state, state.config.approveEndpoint, payload)
      .then(function (result) {
        state.data = result;
        state.busyAction = "";
        initializeEditableState(state);
        setFlash(
          state,
          "success",
          result && result.order
            ? "Order approved and completed successfully."
            : "Draft order approved."
        );
        renderApp(state);
      })
      .catch(function (error) {
        state.busyAction = "";
        setFlash(
          state,
          "error",
          error && error.message ? error.message : "Unable to approve order."
        );
        renderApp(state);
      });
  }

  function handleReject(state) {
    if (state.busyAction) return;

    if (!window.confirm("Reject and delete this draft order?")) {
      return;
    }

    var rejectionReason = getRejectionReason(state);

    state.busyAction = "reject";
    renderApp(state);

    postAction(state, state.config.rejectEndpoint, {
      draftOrderId: state.params.draftOrderId,
      token: state.params.token,
      rejectionReason: rejectionReason,
    })
      .then(function (result) {
        state.busyAction = "";
        state.deleted = true;
        state.deletedInfo = result;
        setFlash(state, "success", "");
        renderApp(state);
      })
      .catch(function (error) {
        state.busyAction = "";
        setFlash(
          state,
          "error",
          error && error.message ? error.message : "Unable to reject order."
        );
        renderApp(state);
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();

